import React, { useState, useEffect } from 'react';
import { DbViewerModalProps } from '../../types';
import EditableCell from '../widgets/EditableCell';
import { testCasesApi, handleApiError } from '../../config/api';
import axios from 'axios';

const getApiBaseUrl = () => {
    if (process.env.REACT_APP_API_BASE_URL) {
        return process.env.REACT_APP_API_BASE_URL;
    }
    if (process.env.NODE_ENV === "development") {
        return "http://localhost:8000";
    }
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return "http://localhost:8000";
    }
    const apiHost = hostname.replace('frontend-alpha', 'orchestrator');
    return `https://${apiHost}`;
};
const API_BASE_URL = getApiBaseUrl();
//const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';


// FIXED: Extended props interface to include setDbTableData
interface ExtendedDbViewerModalProps extends DbViewerModalProps {
    setDbTableData?: (data: any[]) => void; // Make it optional
    setDbTableHeaders?: (headers: string[]) => void;
    fetchTestCases?: () => Promise<void>; // Add this to refresh agent count
}

const DbViewerModal: React.FC<ExtendedDbViewerModalProps> = ({
    isOpen,
    onClose,
    dbTableData,
    dbTableHeaders,
    filteredDbData,
    dbFilter,
    setDbFilter,
    isDbEditMode,
    setIsDbEditMode,
    selectedDbRows,
    onSelectDbRow,
    onSelectAllDbRows,
    onBulkDelete,
    onBulkDuplicate,
    onEditSelectedDbRow,
    onDbFieldEdit,
    setDbTableData,
    fetchTestCases,
    setDbTableHeaders
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [operationStatus, setOperationStatus] = useState<string | null>(null);

    // FIXED: Add separate state for agent filter and type filter:
    const [agentFilter, setAgentFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [localFilteredData, setLocalFilteredData] = useState<any[]>([]);

    const [selectedTable, setSelectedTable] = useState<'test-cases' | 'test-executions' | 'defects' | 'agents'>('test-cases');
    const [error, setError] = useState<string | null>(null);

    // FIXED: Apply both filters
    useEffect(() => {
        const applyFilters = (data: any[]) => {
            if (!data) return [];

            let filtered = data;

            // Apply type filter
            if (typeFilter !== 'all') {
                filtered = filtered.filter(row => row.type === typeFilter);
            }

            // Apply agent filter
            if (agentFilter !== 'all') {
                filtered = filtered.filter(row => row.agent_name === agentFilter);
            }

            return filtered;
        };

        const filtered = applyFilters(dbTableData);
        setLocalFilteredData(filtered);
    }, [dbTableData, typeFilter, agentFilter]);


    useEffect(() => {
        if (isOpen) {
            // When the modal opens, always default to showing test-cases
            setSelectedTable('test-cases');
            fetchData('test-cases');
        }
    }, [isOpen]); // Only runs when the modal is opened

    // FIXED: Enhanced bulk delete with dynamic widget update
    const handleRealBulkDelete = async () => {
        if (selectedDbRows.length === 0) {
            setOperationStatus(' No rows selected for deletion');
            return;
        }

        // ADD: Check if we have valid data
        if (localFilteredData.length === 0) {
            setOperationStatus(' No data available for deletion');
            return;
        }

        // Get IDs of selected test cases - FIXED: Use the actual ID field
        const selectedTestCases = selectedDbRows.map(index => localFilteredData[index]);
        const validTestCases = selectedTestCases.filter(tc => tc && tc.id); // ADD THIS LINE
        if (validTestCases.length === 0) {
            setOperationStatus(' No valid test cases found for deletion');
            setIsLoading(false);
            return;
        }


        const confirmed = window.confirm(
            `DANGER: This will permanently delete ${validTestCases.length} test cases from the database.\n\n` +
            `This action cannot be undone. Are you absolutely sure?`
        );

        if (!confirmed) return;

        setIsLoading(true);
        setOperationStatus(' Deleting test cases from database...');

        try {

            //const testCaseIds = selectedTestCases.map(tc => tc.id || tc.name);
            const testCaseIds = validTestCases.map(tc => tc.id); // CHANGE: use validTestCases
            const agentName = validTestCases[0]?.agent_name || 'unknown'; // CHANGE: use validTestCases


            //const testCaseIds = selectedTestCases.map(tc => tc.id);
            //const agentName = selectedTestCases[0]?.agent_name || 'unknown';

            //console.log('Debug - Attempting to delete test case IDs:', testCaseIds);

            // Call real API to delete test cases
            const result = await testCasesApi.bulkDelete(testCaseIds, agentName);

            if (result.status === 'success') {
                setOperationStatus(`Successfully deleted ${validTestCases.length} test cases`);

                // FIXED: Update the UI immediately by removing deleted items
                if (setDbTableData && typeof setDbTableData === 'function') {
                    //const remainingData = dbTableData.filter(row => !testCaseIds.includes(row.id || row.name));
                    const remainingData = dbTableData.filter(row => !testCaseIds.includes(row.id));
                    //const remainingData = dbTableData.filter(row => !testCaseIds.includes(row.id));
                    setDbTableData(remainingData);
                } else {
                    // Fallback: Refresh the page if setDbTableData is not available
                    //console.log('setDbTableData not available, refreshing page...');
                    setTimeout(() => window.location.reload(), 1000);
                }

                // FIXED: Refresh the widget count dynamically
                if (fetchTestCases && typeof fetchTestCases === 'function') {
                    await fetchTestCases();
                    //console.log('Widget count refreshed after deletion');
                } else {
                    //console.log(' fetchTestCases not available, widget count may be stale');
                }

                // Clear selection
                selectedDbRows.forEach(index => onSelectDbRow(index, false));

                // Call parent's bulk delete handler to update UI if needed
                if (onBulkDelete) {
                    await onBulkDelete();
                }

                // Auto-hide status after 3 seconds
                setTimeout(() => setOperationStatus(null), 3000);
            } else {
                throw new Error(result.message || 'Delete operation failed');
            }
        } catch (error: any) {
            console.error(' Bulk delete error details:', error);

            // Show real error message
            let errorMessage = 'Unknown error occurred';

            if (error.response?.data?.detail) {
                errorMessage = error.response.data.detail;
            } else if (error.message) {
                errorMessage = error.message;
            }

            setOperationStatus(` Delete failed: ${errorMessage}`);
            console.error('Full error object:', error);
        } finally {
            setIsLoading(false);
        }
    };

/*
    // Replace your existing fetchData function with this one
    const fetchData = async (table: 'test-cases' | 'test-executions' | 'defects') => {
        setIsLoading(true);
        setOperationStatus(`Loading data from ${table}...`);
        setError(null);
        try {
            const response = await axios.get(`${API_BASE_URL}/db/${table}`);
            const data = response.data || [];
            if (data.length > 0) {
                // This is the key fix: it updates the parent state
                if (setDbTableData) {
                    const headers = Object.keys(data[0]);
                    // Ensure 'id' is always first for consistency
                    if (dbTableHeaders) setDbTableHeaders(['id', ...headers.filter(h => h !== 'id')]);
                    setDbTableData(data);
                }
                setOperationStatus(null); // Clear loading message
            } else {
                // Also update parent state for empty results
                if (setDbTableData) {
                    setDbTableData([]);
                    if (dbTableHeaders) setDbTableHeaders(['Info']);
                    setDbTableData([{ 'Info': `No data found in ${table}.` }]);
                }
                setOperationStatus(null);
            }
        } catch (err: any) {
            const errorMessage = handleApiError(err);
            setOperationStatus(`Failed to fetch data: ${errorMessage}`);
            // Clear data on error
            if (setDbTableData) {
                setDbTableData([]);
                if (dbTableHeaders) setDbTableHeaders([]);
            }
        } finally {
            setIsLoading(false);
        }
    };
    */

    const fetchData = async (table: 'test-cases' | 'test-executions' | 'defects' | 'agents') => {
        setIsLoading(true);
        setOperationStatus(`Loading data from ${table}...`);
        setError(null);
        try {
            // Handle agents differently as it comes from agent-manager
            const endpoint = table === 'agents'
                ? `${API_BASE_URL}/agent-manager/agents/intelligent`
                : `${API_BASE_URL}/db/${table}`;

            const response = await axios.get(endpoint);
            const data = table === 'agents'
                ? response.data.agents || []
                : response.data || [];

            if (data.length > 0) {
                // This is the key fix: it updates the parent state
                if (setDbTableData) {
                    const headers = Object.keys(data[0]);
                    // Ensure 'id' is always first for consistency
                    // For agents, map 'agent_id' to 'id' for consistency
                    if (table === 'agents') {
                        const mappedData = data.map((agent: any) => ({
                            id: agent.agent_id,
                            ...agent
                        }));
                        if (dbTableHeaders) setDbTableHeaders(['id', ...headers.filter(h => h !== 'agent_id' && h !== 'id')]);
                        setDbTableData(mappedData);
                    } else {
                        if (dbTableHeaders) setDbTableHeaders(['id', ...headers.filter(h => h !== 'id')]);
                        setDbTableData(data);
                    }
                }
                setOperationStatus(null); // Clear loading message
            } else {
                // Also update parent state for empty results
                if (setDbTableData) {
                    setDbTableData([]);
                    if (dbTableHeaders) setDbTableHeaders(['Info']);
                    setDbTableData([{ 'Info': `No data found in ${table}.` }]);
                }
                setOperationStatus(null);
            }
        } catch (err: any) {
            const errorMessage = handleApiError(err);
            setOperationStatus(`Failed to fetch data: ${errorMessage}`);
            // Clear data on error
            if (setDbTableData) {
                setDbTableData([]);
                if (dbTableHeaders) setDbTableHeaders([]);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleTableChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newTable = event.target.value as ('test-cases' | 'test-executions' | 'defects' | 'agents');
        setSelectedTable(newTable);
        // When the radio button changes, we trigger a new data fetch
        fetchData(newTable);
    };

    // Real bulk duplicate implementation
    const handleRealBulkDuplicate = async () => {
        if (selectedDbRows.length === 0) {
            setOperationStatus(' No rows selected for duplication');
            return;
        }

        const confirmed = window.confirm(
            ` Create ${selectedDbRows.length} duplicate test cases?\n\n` +
            `This will copy the selected test cases with "_copy" suffix.`
        );

        if (!confirmed) return;

        setIsLoading(true);
        setOperationStatus(' Duplicating test cases in database...');

        try {
            // Get IDs of selected test cases
            const selectedTestCases = selectedDbRows.map(index => localFilteredData[index]);
            const testCaseIds = selectedTestCases.map(tc => tc.id);
            const agentName = selectedTestCases[0]?.agent_name || 'unknown';

            // Call real API to duplicate test cases
            const result = await testCasesApi.bulkDuplicate(testCaseIds, agentName);

            if (result.status === 'success') {
                setOperationStatus(`Successfully duplicated ${selectedDbRows.length} test cases`);

                // Call parent's bulk duplicate handler to update UI
                await onBulkDuplicate();

                // Clear selection
                selectedDbRows.forEach(index => onSelectDbRow(index, false));

                // Auto-hide status after 3 seconds
                setTimeout(() => setOperationStatus(null), 3000);
            } else {
                throw new Error(result.message || 'Duplicate operation failed');
            }
        } catch (error: any) {
            const errorMessage = handleApiError(error);
            setOperationStatus(` Duplication failed: ${errorMessage}`);
            console.error('Bulk duplicate error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Real field edit implementation
    const handleRealDbFieldEdit = async (rowIndex: number, field: string, newValue: any) => {
        const originalRow = localFilteredData[rowIndex];
        if (!originalRow) {
            setOperationStatus(' Row not found for editing');
            return;
        }

        setIsLoading(true);
        setOperationStatus(` Saving ${field} change to database...`);

        try {
            // Call real API to update field
            const result = await testCasesApi.updateField(
                originalRow.name || originalRow.id,
                field,
                newValue,
                originalRow.agent_name
            );

            if (result.status === 'success') {
                setOperationStatus(`${field} updated successfully`);

                // Call parent's field edit handler to update UI
                await onDbFieldEdit(rowIndex, field, newValue);

                // Auto-hide status after 2 seconds
                setTimeout(() => setOperationStatus(null), 2000);
            } else {
                throw new Error(result.message || 'Field update failed');
            }
        } catch (error: any) {
            const errorMessage = handleApiError(error);
            setOperationStatus(` Update failed: ${errorMessage}`);
            console.error('Field edit error:', error);

            // Revert UI change on error - reload data
            window.location.reload();
        } finally {
            setIsLoading(false);
        }
    };

    // Enhanced cancel edit mode with unsaved changes warning
    const handleCancelEditMode = () => {
        const hasUnsavedChanges = selectedDbRows.length > 0;

        if (hasUnsavedChanges) {
            const confirmed = window.confirm(
                ' You have selected rows. Exiting edit mode will clear your selection.\n\n' +
                'Continue?'
            );
            if (!confirmed) return;
        }

        setIsDbEditMode(false);
        // Clear all selections
        localFilteredData.forEach((_, index) => onSelectDbRow(index, false));
        setOperationStatus(null);
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            {/*<div className="modal-content data-editor-modal" onClick={(e) => e.stopPropagation()}
                 style={{width: '95vw', maxWidth: '1400px'}}>*/}

            <div className="modal-content fullscreen-modal dbviewer-modal" style={{ height: 'calc(100vh - 80px)', marginTop: 40, marginBottom: 40 }} onClick={(e) => e.stopPropagation()}>

                <h2 style={{ marginBottom: '0.5rem' }}>Database</h2>
                <p style={{ marginTop: 0, marginBottom: '0.75rem' }}>
                    View and edit all test cases directly from the database. Changes are saved automatically to the
                    backend.
                </p>

                <div className="table-selector" style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginBottom: '1.5rem', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
                    {(['test-cases', 'test-executions', 'defects', 'agents'] as const).map(table => (
                        <label key={table} className="radio-button-label">
                            <input
                                type="radio"
                                name="table-select"
                                value={table}
                                checked={selectedTable === table}
                                onChange={handleTableChange}
                                disabled={isLoading}
                            />
                            <span>{table.replace('-', ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                        </label>
                    ))}
                </div>

                {/* Operation Status Display */}
                {operationStatus && (
                    <div style={{
                        padding: '0.75rem',
                        marginBottom: '1rem',
                        borderRadius: '6px',
                        backgroundColor: operationStatus.includes('') ? '#f8d7da' :
                            operationStatus.includes('') ? '#fff3cd' : '#d4edda',
                        color: operationStatus.includes('') ? '#721c24' :
                            operationStatus.includes('') ? '#856404' : '#155724',
                        border: `1px solid ${operationStatus.includes('') ? '#f1c2c7' :
                            operationStatus.includes('') ? '#ffeaa7' : '#c3e6cb'}`,
                        fontWeight: '500'
                    }}>
                        {operationStatus}
                    </div>
                )}

                {/* FIXED: Separate Filter Controls */}
                <div className="db-filters"
                    style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'center' }}>
                    <label>
                        Filter by Type:
                        <select
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                            style={{ marginLeft: '0.5rem', padding: '0.25rem' }}
                            disabled={isLoading}
                        >
                            <option value="all">All Types</option>
                            <option value="Functional">Functional</option>
                            <option value="Reliability">Reliability</option>
                            <option value="Guardrail">Guardrail</option>
                            <option value="Compliance">Compliance</option>
                        </select>
                    </label>

                    <label>
                        Filter by Agent:
                        <select
                            value={agentFilter}
                            onChange={(e) => setAgentFilter(e.target.value)}
                            style={{ marginLeft: '0.5rem', padding: '0.25rem' }}
                            disabled={isLoading}
                        >
                            <option value="all">All Agents</option>
                            {[...new Set(dbTableData.map(row => row.agent_name))].filter(Boolean).map(agent => (
                                <option key={agent} value={agent}>{agent}</option>
                            ))}
                        </select>
                    </label>

                    <span style={{ fontSize: '0.9rem', color: '#666' }}>
                        Showing {localFilteredData.length} of {dbTableData.length} records
                    </span>

                    <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
                        {/* This entire block is now conditional */}
                        {selectedTable === 'test-cases' && (
                            <>
                                {isDbEditMode && selectedDbRows.length > 0 && (
                                    <>
                                        {selectedDbRows.length === 1 && (
                                            <button
                                                onClick={onEditSelectedDbRow}
                                                className="pushable push-secondary btn-sm"
                                                disabled={isLoading}
                                            >
                                                <span className="shadow"></span><span className="edge"></span><span className="front">Edit Selected</span>
                                            </button>
                                        )}
                                        <button
                                            onClick={handleRealBulkDuplicate}
                                            className="pushable push-warning btn-sm"
                                            disabled={isLoading}
                                        >
                                            <span className="shadow"></span><span className="edge"></span><span className="front">Duplicate ({selectedDbRows.length})</span>
                                        </button>
                                        <button
                                            onClick={handleRealBulkDelete}
                                            className="pushable push-danger btn-sm"
                                            disabled={isLoading}
                                        >
                                            <span className="shadow"></span><span className="edge"></span><span className="front">🗑️ Delete ({selectedDbRows.length})</span>
                                        </button>
                                    </>
                                )}
                                <button
                                    onClick={isDbEditMode ? handleCancelEditMode : () => setIsDbEditMode(true)}
                                    className={`pushable ${isDbEditMode ? 'push-danger' : 'push-primary'} btn-sm`}
                                    disabled={isLoading}
                                >
                                    <span className="shadow"></span>
                                    <span className="edge"></span>
                                    <span className="front">
                                        {isDbEditMode ? 'Cancel Edit' : 'Edit Mode'}
                                    </span>
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Loading Overlay */}
                {isLoading && (
                    <div style={{
                        position: 'absolute',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(238, 245, 244, 0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                        borderRadius: '6px'
                    }}>
                        <div style={{
                            padding: '2rem',
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            textAlign: 'center'
                        }}>
                            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}></div>
                            <div style={{ fontSize: '1.1rem', fontWeight: '500' }}>Processing...</div>
                        </div>
                    </div>
                )}

                {/* Data Table */}
                <div style={{
                    height: '90vh',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    position: 'relative',
                    overflow: 'auto' // FIXED: Remove overflow to prevent double scrollbars
                }}>
                    <div className="editable-table-container" style={{
                        height: '100%',
                        overflow: 'auto' // Only this container should scroll
                    }}>
                        {localFilteredData.length > 0 ? (
                            <table className="results-table editable-table"
                                style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa' }}>
                                    <tr>
                                        {isDbEditMode && (
                                            <th style={{ padding: '0.5rem', textAlign: 'center' }}>
                                                <input
                                                    type="checkbox"
                                                    onChange={onSelectAllDbRows}
                                                    checked={localFilteredData.length > 0 && selectedDbRows.length === localFilteredData.length}
                                                    disabled={isLoading || localFilteredData.length === 0}
                                                />
                                            </th>
                                        )}
                                        {dbTableHeaders.map(h => (
                                            <th key={h} style={{
                                                padding: '0.5rem',
                                                borderBottom: '1px solid #ddd',
                                                textAlign: 'center',
                                                minWidth: '150px'
                                            }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {localFilteredData.map((row, rIndex) => (
                                        <tr key={row.id || rIndex} style={{
                                            borderBottom: '1px solid #eee',
                                            backgroundColor: selectedDbRows.includes(rIndex) ? '#e3f2fd' : 'transparent',
                                            opacity: isLoading ? 0.6 : 1
                                        }}>
                                            {isDbEditMode && (
                                                <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedDbRows.includes(rIndex)}
                                                        onChange={(e) => onSelectDbRow(rIndex, e.target.checked)}
                                                        disabled={isLoading}
                                                    />
                                                </td>
                                            )}
                                            {dbTableHeaders.map(h => (
                                                <td key={`${rIndex}-${h}`} style={{
                                                    minWidth: '150px',
                                                    maxWidth: '300px',
                                                    padding: '0.5rem',
                                                    fontSize: '0.8rem',
                                                    verticalAlign: 'top'
                                                }}>
                                                    {isDbEditMode && h !== 'id' && h !== 'created_at' ? (
                                                        <EditableCell
                                                            value={row[h]}
                                                            onSave={(newValue) => handleRealDbFieldEdit(rIndex, h, newValue)}
                                                            disabled={isLoading}
                                                        />
                                                    ) : (
                                                        <pre style={{
                                                            margin: 0,
                                                            whiteSpace: 'pre-wrap',
                                                            wordBreak: 'break-word'
                                                        }}>
                                                            {typeof row[h] === 'object' && row[h] !== null ?
                                                                JSON.stringify(row[h], null, 2) :
                                                                String(row[h])
                                                            }
                                                        </pre>
                                                    )}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                                No data matches the current filter.
                            </p>
                        )}
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="modal-actions" style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <button className="pushable push-secondary btn-sm" onClick={onClose}>
                        <span className="shadow"></span>
                        <span className="edge"></span>
                        <span className="front">Close</span>
                    </button>
                    <button className="pushable push-primary btn-sm" onClick={() => {
                        setOperationStatus('All database changes have been saved automatically!');
                        setTimeout(() => onClose(), 1500);
                    }}>
                        <span className="shadow"></span>
                        <span className="edge"></span>
                        <span className="front">Done</span>
                    </button>
                </div>
            </div>
        </div >
    );
};

export default DbViewerModal;